package br.com.heyagenda.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import br.com.heyagenda.di.AppModule
import br.com.heyagenda.domain.model.StudioService
import br.com.heyagenda.presentation.booking.PublicBookingUiState
import br.com.heyagenda.ui.components.AuraButton
import br.com.heyagenda.ui.components.AuraButtonSize
import br.com.heyagenda.ui.components.AuraButtonVariant
import br.com.heyagenda.ui.components.AuraCard
import br.com.heyagenda.ui.components.AuraPillBadge
import br.com.heyagenda.ui.components.AuraShimmerBox
import br.com.heyagenda.ui.components.AuraTextField
import br.com.heyagenda.ui.components.PhoneVisualTransformation
import br.com.heyagenda.ui.theme.AuraAmber
import br.com.heyagenda.ui.theme.AuraBackground
import br.com.heyagenda.ui.theme.AuraBorderHover
import br.com.heyagenda.ui.theme.AuraBorderSubtle
import br.com.heyagenda.ui.theme.AuraCardSurface
import br.com.heyagenda.ui.theme.AuraEmerald
import br.com.heyagenda.ui.theme.AuraOnEmerald
import br.com.heyagenda.ui.theme.AuraModalSurface
import br.com.heyagenda.ui.theme.AuraPillBg
import br.com.heyagenda.ui.theme.AuraPrimaryButtonBg
import br.com.heyagenda.ui.theme.AuraPrimaryButtonText
import br.com.heyagenda.ui.theme.AuraTextMuted
import br.com.heyagenda.ui.theme.AuraTextPrimary
import br.com.heyagenda.ui.theme.AuraTextSecondary
import br.com.heyagenda.ui.theme.AuraTheme
import cafe.adriel.voyager.core.model.rememberScreenModel
import cafe.adriel.voyager.core.screen.Screen

import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextAlign
import heyagenda.shared.generated.resources.Res
import heyagenda.shared.generated.resources.arrow_left
import heyagenda.shared.generated.resources.arrow_right
import heyagenda.shared.generated.resources.calendar_blank
import heyagenda.shared.generated.resources.check
import heyagenda.shared.generated.resources.clock
import heyagenda.shared.generated.resources.instagram_logo
import heyagenda.shared.generated.resources.map_pin_simple
import heyagenda.shared.generated.resources.plus
import heyagenda.shared.generated.resources.whatsapp_logo
import heyagenda.shared.generated.resources.x
import org.jetbrains.compose.resources.painterResource

private fun parseAccentColor(hex: String?, fallback: Color): Color {
    if (hex.isNullOrBlank()) return fallback
    return try {
        val clean = hex.removePrefix("#").trim()
        if (clean.length == 6) {
            Color(0xFF000000 or clean.toLong(16))
        } else if (clean.length == 8) {
            Color(clean.toLong(16))
        } else fallback
    } catch (_: Throwable) {
        fallback
    }
}

data class PublicBookingScreen(
    val slugOrUid: String = "",
    val initialClientName: String? = null,
    val initialClientPhone: String? = null,
    val onBookingSuccess: ((clientName: String, clientPhone: String) -> Unit)? = null,
    val onHomeClick: (() -> Unit)? = null
) : Screen {

    @Composable
    override fun Content() {
        val screenModel = rememberScreenModel(slugOrUid) { AppModule.providePublicBookingScreenModel(slugOrUid) }
        val uiState by screenModel.uiState.collectAsState()

        androidx.compose.runtime.LaunchedEffect(initialClientName, initialClientPhone) {
            if (!initialClientName.isNullOrBlank() && uiState.clientName.isBlank()) {
                screenModel.updateClientName(initialClientName)
            }
            if (!initialClientPhone.isNullOrBlank() && uiState.clientPhone.isBlank()) {
                screenModel.updateClientPhone(initialClientPhone)
            }
        }

        androidx.compose.runtime.LaunchedEffect(uiState.confirmedAppointment) {
            val confirmed = uiState.confirmedAppointment
            if (confirmed != null) {
                onBookingSuccess?.invoke(confirmed.clientName, confirmed.clientPhone.orEmpty())
            }
        }

        if (uiState.isLoading) {
            br.com.heyagenda.ui.components.PublicBookingSkeleton()
            return
        }

        if (uiState.notFound) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(AuraBackground)
                    .padding(24.dp),
                contentAlignment = Alignment.Center
            ) {
                AuraCard(modifier = Modifier.fillMaxWidth()) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = "Estúdio não encontrado",
                            fontSize = 20.sp,
                            fontWeight = FontWeight.Bold,
                            color = AuraTextPrimary
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = if (slugOrUid.isNotBlank()) {
                                "O link \"$slugOrUid\" não está associado a nenhum profissional cadastrado no Hey Agenda."
                            } else {
                                "Nenhum link ou identificador de estúdio foi fornecido."
                            },
                            fontSize = 14.sp,
                            color = AuraTextMuted,
                            textAlign = TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(24.dp))
                        if (onHomeClick != null) {
                            AuraButton(
                                text = "Voltar para o Início",
                                onClick = onHomeClick
                            )
                        }
                    }
                }
            }
            return
        }

        PublicBookingContent(
            uiState = uiState,
            onServiceToggle = { screenModel.toggleService(it) },
            onServiceRemove = { screenModel.removeService(it) },
            onOpenBookingModal = { screenModel.openBookingModal() },
            onDateSelect = { dateIso, dateText -> screenModel.selectDate(dateIso, dateText) },
            onSlotSelect = { screenModel.selectTimeSlot(it) },
            onNameChange = { screenModel.updateClientName(it) },
            onPhoneChange = { screenModel.updateClientPhone(it) },
            onNotesChange = { screenModel.updateClientNotes(it) },
            onPaymentMethodChange = { screenModel.updatePaymentMethod(it) },
            onCloseModal = { screenModel.closeModal() },
            onSubmitBooking = { screenModel.submitBooking() },
            onResetBooking = { screenModel.resetBooking() },
            onHomeClick = onHomeClick
        )
    }
}

@Composable
fun PublicBookingContent(
    uiState: PublicBookingUiState,
    onServiceToggle: (StudioService) -> Unit,
    onServiceRemove: (StudioService) -> Unit,
    onOpenBookingModal: () -> Unit,
    onDateSelect: (dateIso: String, dateText: String) -> Unit,
    onSlotSelect: (String) -> Unit,
    onNameChange: (String) -> Unit,
    onPhoneChange: (String) -> Unit,
    onNotesChange: (String) -> Unit,
    onPaymentMethodChange: (String) -> Unit,
    onCloseModal: () -> Unit,
    onSubmitBooking: () -> Unit,
    onResetBooking: () -> Unit,
    onHomeClick: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    when {
        uiState.confirmedAppointment != null -> {
            PublicBookingSuccessView(
                appointment = uiState.confirmedAppointment,
                studioName = uiState.studioName,
                totalDuration = uiState.formattedDuration,
                selectedDate = uiState.selectedDateText,
                address = uiState.address,
                instagramHandle = uiState.instagramHandle,
                onDone = onResetBooking,
                modifier = modifier
            )
        }
        uiState.isBookingModalOpen && uiState.selectedServices.isNotEmpty() -> {
            PublicBookingConfirmationView(
                uiState = uiState,
                onBackToCatalog = onCloseModal,
                onRemoveService = onServiceRemove,
                onDateSelect = onDateSelect,
                onSlotSelect = onSlotSelect,
                onNameChange = onNameChange,
                onPhoneChange = onPhoneChange,
                onNotesChange = onNotesChange,
                onPaymentMethodChange = onPaymentMethodChange,
                onSubmitBooking = onSubmitBooking,
                modifier = modifier
            )
        }
        else -> {
            PublicBookingCatalogView(
                uiState = uiState,
                onServiceToggle = onServiceToggle,
                onOpenConfirmation = onOpenBookingModal,
                onHomeClick = onHomeClick,
                modifier = modifier
            )
        }
    }
}

@Composable
fun PublicBookingCatalogView(
    uiState: PublicBookingUiState,
    onServiceToggle: (StudioService) -> Unit,
    onOpenConfirmation: () -> Unit,
    onHomeClick: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    val scrollState = rememberScrollState()
    val uriHandler = LocalUriHandler.current
    val defaultAccent = AuraEmerald
    val studioAccent = remember(uiState.themeAccentColor, defaultAccent) {
        parseAccentColor(uiState.themeAccentColor, defaultAccent)
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(AuraBackground)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(scrollState)
        ) {
            // 1. Studio Header Card
            PublicStudioHeaderWidget(
                studioName = uiState.studioName,
                professionalName = uiState.professionalName,
                specialty = uiState.specialty,
                bio = uiState.bio,
                themeAccentColor = studioAccent,
                instagramHandle = uiState.instagramHandle,
                address = uiState.address,
                announcementBanner = uiState.announcementBanner,
                onHomeClick = onHomeClick
            )

            // Alerta de Limite Atingido para plano Grátis
            if (uiState.hasReachedMonthlyLimit) {
                Spacer(modifier = Modifier.height(12.dp))
                MonthlyLimitReachedCard(
                    studioName = uiState.studioName,
                    phone = uiState.professionalPhone,
                    instagramHandle = uiState.instagramHandle,
                    accentColor = studioAccent
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp)
            ) {
                // Section Title & Selection Counter
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "Serviços & Procedimentos",
                            fontSize = 18.sp,
                            fontWeight = FontWeight.Bold,
                            color = AuraTextPrimary
                        )
                        Text(
                            text = if (uiState.hasReachedMonthlyLimit) "Consulte os serviços abaixo e agende diretamente pelo WhatsApp" else "Selecione um ou mais serviços para o seu atendimento",
                            fontSize = 12.sp,
                            color = AuraTextMuted,
                            modifier = Modifier.padding(top = 2.dp)
                        )
                    }

                    if (uiState.selectedServices.isNotEmpty()) {
                        Box(
                            modifier = Modifier
                                .background(studioAccent.copy(alpha = 0.15f), RoundedCornerShape(12.dp))
                                .border(1.dp, studioAccent.copy(alpha = 0.4f), RoundedCornerShape(12.dp))
                                .padding(horizontal = 10.dp, vertical = 5.dp)
                        ) {
                            Text(
                                text = "${uiState.selectedServices.size} selecionado(s)",
                                fontSize = 11.sp,
                                fontWeight = FontWeight.Bold,
                                color = studioAccent
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(14.dp))

                // Service Catalog Cards
                if (uiState.isLoadingServices) {
                    repeat(3) {
                        AuraCard(
                            modifier = Modifier.fillMaxWidth(),
                            cornerRadius = 20.dp
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                verticalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                                        AuraShimmerBox(width = 160.dp, height = 18.dp, shape = RoundedCornerShape(6.dp))
                                        AuraShimmerBox(width = 90.dp, height = 12.dp, shape = RoundedCornerShape(4.dp))
                                    }
                                    AuraShimmerBox(width = 70.dp, height = 20.dp, shape = RoundedCornerShape(6.dp))
                                }
                                AuraShimmerBox(height = 12.dp, shape = RoundedCornerShape(4.dp))
                            }
                        }
                        Spacer(modifier = Modifier.height(12.dp))
                    }
                } else if (uiState.services.isEmpty()) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 32.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "Nenhum serviço disponível para agendamento no momento.",
                            color = AuraTextMuted,
                            fontSize = 14.sp
                        )
                    }
                } else {
                    uiState.services.forEach { service ->
                        val isSelected = uiState.selectedServices.contains(service)
                        ServiceCatalogCard(
                            service = service,
                            isSelected = isSelected,
                            onToggle = { onServiceToggle(service) },
                            accentColor = studioAccent
                        )
                        Spacer(modifier = Modifier.height(12.dp))
                    }
                }

                // Watermark Hey Agenda para plano Grátis (recurso Pro: sem marca d'água)
                if (!uiState.isPro) {
                    Spacer(modifier = Modifier.height(20.dp))
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(bottom = 16.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(6.dp),
                            modifier = Modifier
                                .clip(RoundedCornerShape(20.dp))
                                .background(AuraPillBg)
                                .border(1.dp, AuraBorderSubtle, RoundedCornerShape(20.dp))
                                .clickable {
                                    if (onHomeClick != null) onHomeClick()
                                    else uriHandler.openUri("https://heyagenda.com.br")
                                }
                                .padding(horizontal = 14.dp, vertical = 7.dp)
                        ) {
                            Text(
                                text = "Agendamento online seguro por",
                                fontSize = 11.sp,
                                color = AuraTextMuted
                            )
                            Text(
                                text = "Hey Agenda",
                                fontSize = 11.sp,
                                fontWeight = FontWeight.Bold,
                                color = AuraEmerald
                            )
                        }
                    }
                }

                // Space at bottom for floating summary bar
                Spacer(modifier = Modifier.height(if (uiState.selectedServices.isNotEmpty()) 120.dp else 40.dp))
            }
        }

        // Floating Bottom Summary Bar (Carrinho)
        if (uiState.selectedServices.isNotEmpty()) {
            FloatingBookingSummaryBar(
                selectedCount = uiState.selectedServices.size,
                formattedDuration = uiState.formattedDuration,
                totalPriceFormatted = uiState.totalPriceFormatted,
                onContinueClick = if (uiState.hasReachedMonthlyLimit) {
                    {
                        val phoneDigits = uiState.professionalPhone?.filter { it.isDigit() }.orEmpty()
                        if (phoneDigits.isNotBlank()) {
                            val national = if (phoneDigits.startsWith("55")) phoneDigits else "55$phoneDigits"
                            val servicesText = uiState.selectedServiceNames
                            val text = "Olá! Vi seus serviços no Hey Agenda ($servicesText - ${uiState.totalPriceFormatted}) e gostaria de agendar um horário com você."
                            uriHandler.openUri("https://wa.me/$national?text=${encodeUri(text)}")
                        } else {
                            onOpenConfirmation()
                        }
                    }
                } else onOpenConfirmation,
                buttonText = if (uiState.hasReachedMonthlyLimit) "Agendar no WhatsApp" else "Agendar",
                isWhatsAppAction = uiState.hasReachedMonthlyLimit,
                accentColor = studioAccent,
                modifier = Modifier.align(Alignment.BottomCenter)
            )
        }
    }
}

@Composable
fun MonthlyLimitReachedCard(
    studioName: String,
    phone: String?,
    instagramHandle: String?,
    accentColor: Color
) {
    val uriHandler = LocalUriHandler.current
    val phoneDigits = phone?.filter { it.isDigit() }.orEmpty()
    val cleanNational = if (phoneDigits.startsWith("55")) phoneDigits else if (phoneDigits.isNotBlank()) "55$phoneDigits" else ""

    AuraCard(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp),
        cornerRadius = 20.dp
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .background(AuraAmber.copy(alpha = 0.08f))
                .border(1.dp, AuraAmber.copy(alpha = 0.35f), RoundedCornerShape(20.dp))
                .padding(18.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Box(
                    modifier = Modifier
                        .size(36.dp)
                        .clip(CircleShape)
                        .background(AuraAmber.copy(alpha = 0.18f)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        painter = painterResource(Res.drawable.calendar_blank),
                        contentDescription = null,
                        tint = AuraAmber,
                        modifier = Modifier.size(18.dp)
                    )
                }
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = "Agendamentos Online Pausados no Mês",
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Bold,
                        color = AuraTextPrimary
                    )
                    Text(
                        text = "Capacidade máxima de agendamentos online atingida neste mês",
                        fontSize = 11.sp,
                        color = AuraTextSecondary
                    )
                }
            }

            Text(
                text = "Este estabelecimento atingiu a cota mensal de agendamentos online automáticos. Você ainda pode consultar os serviços e valores abaixo e agendar diretamente com a equipe:",
                fontSize = 12.sp,
                color = AuraTextSecondary,
                lineHeight = 17.sp
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                if (cleanNational.isNotBlank()) {
                    val defaultMsg = "Olá! Gostaria de agendar um horário no $studioName."
                    AuraButton(
                        text = "Agendar no WhatsApp",
                        onClick = {
                            uriHandler.openUri("https://wa.me/$cleanNational?text=${encodeUri(defaultMsg)}")
                        },
                        modifier = Modifier.weight(1f),
                        variant = AuraButtonVariant.Emerald,
                        size = AuraButtonSize.Small,
                        leadingIcon = {
                            Icon(
                                painter = painterResource(Res.drawable.whatsapp_logo),
                                contentDescription = null,
                                modifier = Modifier.size(14.dp)
                            )
                        }
                    )
                }

                if (!instagramHandle.isNullOrBlank()) {
                    val cleanInsta = instagramHandle.removePrefix("@").trim()
                    AuraButton(
                        text = "Instagram",
                        onClick = {
                            uriHandler.openUri("https://instagram.com/$cleanInsta")
                        },
                        modifier = if (cleanNational.isBlank()) Modifier.fillMaxWidth() else Modifier.weight(0.7f),
                        variant = AuraButtonVariant.Secondary,
                        size = AuraButtonSize.Small,
                        leadingIcon = {
                            Icon(
                                painter = painterResource(Res.drawable.instagram_logo),
                                contentDescription = null,
                                modifier = Modifier.size(14.dp)
                            )
                        }
                    )
                }
            }
        }
    }
}

@Composable
fun FloatingBookingSummaryBar(
    selectedCount: Int,
    formattedDuration: String,
    totalPriceFormatted: String,
    onContinueClick: () -> Unit,
    modifier: Modifier = Modifier,
    accentColor: Color = AuraEmerald,
    buttonText: String = "Agendar",
    isWhatsAppAction: Boolean = false
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .padding(16.dp)
    ) {
        androidx.compose.material3.Surface(
            modifier = Modifier
                .fillMaxWidth()
                .border(
                    width = 1.dp,
                    color = if (AuraTheme.isDark) (if (isWhatsAppAction) AuraEmerald.copy(alpha = 0.6f) else accentColor.copy(alpha = 0.6f)) else AuraBorderHover,
                    shape = RoundedCornerShape(20.dp)
                ),
            color = AuraModalSurface,
            shape = RoundedCornerShape(20.dp),
            shadowElevation = if (AuraTheme.isDark) 8.dp else 16.dp
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 18.dp, vertical = 14.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column {
                    Text(
                        text = "$selectedCount ${if (selectedCount == 1) "serviço selecionado" else "serviços selecionados"}",
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                        color = AuraTextPrimary
                    )

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                        modifier = Modifier.padding(top = 2.dp)
                    ) {
                        Icon(
                            painter = painterResource(Res.drawable.clock),
                            contentDescription = null,
                            tint = AuraTextMuted,
                            modifier = Modifier.size(13.dp)
                        )
                        Text(
                            text = formattedDuration,
                            fontSize = 12.sp,
                            color = AuraTextMuted
                        )
                        Text(
                            text = "•",
                            fontSize = 12.sp,
                            color = AuraTextMuted
                        )
                        Text(
                            text = totalPriceFormatted,
                            fontSize = 14.sp,
                            fontWeight = FontWeight.Bold,
                            color = if (isWhatsAppAction) AuraEmerald else accentColor
                        )
                    }
                }

                val btnBg = if (isWhatsAppAction) AuraEmerald else accentColor
                AuraButton(
                    text = buttonText,
                    onClick = onContinueClick,
                    variant = AuraButtonVariant.Emerald,
                    containerColor = btnBg,
                    contentColor = AuraOnEmerald,
                    leadingIcon = if (isWhatsAppAction) {
                        {
                            Icon(
                                painter = painterResource(Res.drawable.whatsapp_logo),
                                contentDescription = null,
                                modifier = Modifier.size(16.dp)
                            )
                        }
                    } else null,
                    trailingIcon = if (!isWhatsAppAction) {
                        {
                            Icon(
                                painter = painterResource(Res.drawable.arrow_right),
                                contentDescription = null,
                                modifier = Modifier.size(16.dp)
                            )
                        }
                    } else null
                )
            }
        }
    }
}

@Composable
fun PublicStudioHeaderWidget(
    studioName: String,
    professionalName: String,
    specialty: String,
    bio: String,
    themeAccentColor: Color = AuraEmerald,
    instagramHandle: String? = null,
    address: String? = null,
    announcementBanner: String? = null,
    onHomeClick: (() -> Unit)? = null
) {
    val uriHandler = LocalUriHandler.current

    AuraCard(
        modifier = Modifier
            .fillMaxWidth()
            .padding(16.dp),
        cornerRadius = 24.dp,
        borderColor = AuraBorderSubtle
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(20.dp)
        ) {
            if (!announcementBanner.isNullOrBlank()) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 16.dp)
                        .background(themeAccentColor.copy(alpha = 0.15f), RoundedCornerShape(12.dp))
                        .border(1.dp, themeAccentColor.copy(alpha = 0.4f), RoundedCornerShape(12.dp))
                        .padding(horizontal = 14.dp, vertical = 8.dp)
                ) {
                    Text(
                        text = announcementBanner,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = themeAccentColor
                    )
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.weight(1f)
                ) {
                    // Studio Avatar Circle with Brand Accent Online Ring
                    Box(
                        modifier = Modifier
                            .size(52.dp)
                            .background(
                                if (AuraTheme.isDark) AuraPillBg else themeAccentColor.copy(alpha = 0.12f),
                                CircleShape
                            )
                            .border(1.5.dp, themeAccentColor, CircleShape),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = studioName.take(2).uppercase(),
                            fontSize = 18.sp,
                            fontWeight = FontWeight.Bold,
                            color = if (AuraTheme.isDark) AuraTextPrimary else themeAccentColor
                        )
                    }

                    Spacer(modifier = Modifier.width(14.dp))

                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = studioName,
                            fontSize = 20.sp,
                            fontWeight = FontWeight.Bold,
                            color = AuraTextPrimary
                        )

                        if (professionalName.isNotBlank()) {
                            Text(
                                text = "por $professionalName",
                                fontSize = 12.sp,
                                color = AuraTextMuted,
                                modifier = Modifier.padding(top = 2.dp)
                            )
                        }
                    }
                }
            }

            if (specialty.isNotBlank()) {
                Spacer(modifier = Modifier.height(10.dp))
                AuraPillBadge(
                    text = specialty,
                    backgroundColor = themeAccentColor.copy(alpha = 0.15f),
                    textColor = themeAccentColor
                )
            }

            if (bio.isNotBlank()) {
                Spacer(modifier = Modifier.height(10.dp))
                Text(
                    text = bio,
                    fontSize = 12.sp,
                    color = AuraTextMuted,
                    lineHeight = 18.sp
                )
            }

            // Interactive Buttons: Instagram & Google Maps
            val hasInstagram = !instagramHandle.isNullOrBlank()
            val hasAddress = !address.isNullOrBlank()

            if (hasInstagram || hasAddress) {
                Spacer(modifier = Modifier.height(14.dp))
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (hasInstagram && instagramHandle != null) {
                        val cleanHandle = instagramHandle.trim().removePrefix("@")
                        val igUrl = "https://instagram.com/$cleanHandle"
                        Row(
                            modifier = Modifier
                                .clip(RoundedCornerShape(100.dp))
                                .background(if (AuraTheme.isDark) AuraPillBg else themeAccentColor.copy(alpha = 0.08f))
                                .border(1.dp, if (AuraTheme.isDark) AuraBorderSubtle else themeAccentColor.copy(alpha = 0.25f), RoundedCornerShape(100.dp))
                                .clickable {
                                    try {
                                        uriHandler.openUri(igUrl)
                                    } catch (_: Throwable) {}
                                }
                                .padding(horizontal = 12.dp, vertical = 7.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(6.dp)
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.instagram_logo),
                                contentDescription = "Instagram",
                                tint = if (AuraTheme.isDark) Color(0xFFFF5282) else Color(0xFFE1306C),
                                modifier = Modifier.size(15.dp)
                            )
                            Text(
                                text = "@$cleanHandle",
                                fontSize = 12.sp,
                                fontWeight = FontWeight.SemiBold,
                                color = AuraTextPrimary
                            )
                        }
                    }

                    if (hasAddress && address != null) {
                        val cleanAddr = address.trim()
                        val mapsUrl = "https://www.google.com/maps/search/?api=1&query=${encodeUri(cleanAddr)}"
                        Row(
                            modifier = Modifier
                                .clip(RoundedCornerShape(100.dp))
                                .background(if (AuraTheme.isDark) AuraPillBg else themeAccentColor.copy(alpha = 0.08f))
                                .border(1.dp, if (AuraTheme.isDark) AuraBorderSubtle else themeAccentColor.copy(alpha = 0.25f), RoundedCornerShape(100.dp))
                            .clickable {
                                try {
                                    uriHandler.openUri(mapsUrl)
                                } catch (_: Throwable) {}
                            }
                            .padding(horizontal = 12.dp, vertical = 7.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(6.dp)
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.map_pin_simple),
                                contentDescription = "Ver no Google Maps",
                                tint = themeAccentColor,
                                modifier = Modifier.size(15.dp)
                            )
                            Text(
                                text = cleanAddr,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.Medium,
                                color = AuraTextPrimary,
                                maxLines = 1
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun ServiceCatalogCard(
    service: StudioService,
    isSelected: Boolean,
    onToggle: () -> Unit,
    accentColor: Color = AuraEmerald
) {
    val borderColor = if (isSelected) accentColor else AuraBorderSubtle
    val bgColor = if (isSelected) accentColor.copy(alpha = 0.12f) else AuraCardSurface

    AuraCard(
        modifier = Modifier.fillMaxWidth(),
        cornerRadius = 20.dp,
        borderColor = borderColor,
        backgroundColor = bgColor,
        onClick = onToggle
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = service.name,
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        color = AuraTextPrimary
                    )

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        modifier = Modifier.padding(top = 4.dp)
                    ) {
                        Icon(
                            painter = painterResource(Res.drawable.clock),
                            contentDescription = null,
                            tint = AuraTextMuted,
                            modifier = Modifier.size(12.dp)
                        )
                        Text(
                            text = "${service.durationMinutes} min",
                            fontSize = 11.sp,
                            color = AuraTextMuted
                        )
                        if (service.category.isNotBlank()) {
                            Spacer(modifier = Modifier.width(6.dp))
                            Text(
                                text = "•  ${service.category}",
                                fontSize = 11.sp,
                                color = AuraTextMuted
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.width(12.dp))

                Text(
                    text = service.priceFormatted,
                    fontSize = 17.sp,
                    fontWeight = FontWeight.Bold,
                    color = accentColor
                )
            }

            if (service.description.isNotBlank()) {
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = service.description,
                    fontSize = 12.sp,
                    color = AuraTextMuted,
                    lineHeight = 17.sp
                )
            }

            Spacer(modifier = Modifier.height(12.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                if (isSelected) {
                    AuraButton(
                        text = "Selecionado",
                        onClick = onToggle,
                        variant = AuraButtonVariant.Emerald,
                        containerColor = accentColor,
                        contentColor = AuraOnEmerald,
                        size = br.com.heyagenda.ui.components.AuraButtonSize.Small,
                        leadingIcon = {
                            Icon(
                                painter = painterResource(Res.drawable.check),
                                contentDescription = null,
                                tint = AuraOnEmerald,
                                modifier = Modifier.size(14.dp)
                            )
                        }
                    )
                } else {
                    AuraButton(
                        text = "Adicionar",
                        onClick = onToggle,
                        variant = AuraButtonVariant.Secondary,
                        size = br.com.heyagenda.ui.components.AuraButtonSize.Small,
                        leadingIcon = {
                            Icon(
                                painter = painterResource(Res.drawable.plus),
                                contentDescription = null,
                                modifier = Modifier.size(14.dp)
                            )
                        }
                    )
                }
            }
        }
    }
}

@Composable
fun PublicBookingConfirmationView(
    uiState: PublicBookingUiState,
    onBackToCatalog: () -> Unit,
    onRemoveService: (StudioService) -> Unit,
    onDateSelect: (dateIso: String, dateText: String) -> Unit,
    onSlotSelect: (String) -> Unit,
    onNameChange: (String) -> Unit,
    onPhoneChange: (String) -> Unit,
    onNotesChange: (String) -> Unit,
    onPaymentMethodChange: (String) -> Unit,
    onSubmitBooking: () -> Unit,
    modifier: Modifier = Modifier
) {
    val bookingDays = remember {
        br.com.heyagenda.domain.model.CalendarUtils.getNextDays(14)
    }

    LaunchedEffect(bookingDays) {
        if (uiState.selectedDateIso.isBlank() && bookingDays.isNotEmpty()) {
            onDateSelect(bookingDays.first().dateIso, bookingDays.first().label)
        }
    }

    val selectedSlot = uiState.selectedTimeSlot ?: uiState.availableTimeSlots.firstOrNull()
    val scrollState = rememberScrollState()
    val dateScrollState = rememberScrollState()
    val uriHandler = LocalUriHandler.current
    val defaultAccent = AuraEmerald
    val brandAccent = remember(uiState.themeAccentColor, defaultAccent) {
        parseAccentColor(uiState.themeAccentColor, defaultAccent)
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(AuraBackground)
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Header Bar
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .widthIn(max = 680.dp)
                    .padding(horizontal = 16.dp, vertical = 14.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(40.dp)
                        .clip(CircleShape)
                        .background(AuraPillBg)
                        .border(1.dp, AuraBorderSubtle, CircleShape)
                        .clickable { onBackToCatalog() },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        painter = painterResource(Res.drawable.arrow_left),
                        contentDescription = "Voltar para Serviços",
                        tint = AuraTextPrimary,
                        modifier = Modifier.size(18.dp)
                    )
                }

                Spacer(modifier = Modifier.width(14.dp))

                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = "Confirmar Agendamento",
                        fontSize = 18.sp,
                        fontWeight = FontWeight.Bold,
                        color = AuraTextPrimary
                    )
                    Text(
                        text = uiState.studioName,
                        fontSize = 12.sp,
                        color = AuraTextMuted
                    )
                }
            }

            // Scrollable Content
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .verticalScroll(scrollState),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Column(
                    modifier = Modifier
                        .widthIn(max = 680.dp)
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp)
                ) {
                    // 1. Selected Services Card
                    AuraCard(
                        modifier = Modifier.fillMaxWidth(),
                        cornerRadius = 20.dp
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(18.dp)
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(
                                    text = "Serviços Selecionados (${uiState.selectedServices.size})",
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = AuraTextPrimary
                                )

                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                                ) {
                                    Icon(
                                        painter = painterResource(Res.drawable.clock),
                                        contentDescription = null,
                                        tint = AuraTextMuted,
                                        modifier = Modifier.size(13.dp)
                                    )
                                    Text(
                                        text = uiState.formattedDuration,
                                        fontSize = 12.sp,
                                        color = AuraTextMuted
                                    )
                                }
                            }

                            Spacer(modifier = Modifier.height(12.dp))

                            Column(
                                modifier = Modifier.fillMaxWidth(),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                uiState.selectedServices.forEach { s ->
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .background(AuraPillBg, RoundedCornerShape(12.dp))
                                            .border(1.dp, AuraBorderSubtle, RoundedCornerShape(12.dp))
                                            .padding(horizontal = 12.dp, vertical = 10.dp),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            Text(
                                                text = s.name,
                                                fontSize = 14.sp,
                                                fontWeight = FontWeight.Medium,
                                                color = AuraTextPrimary
                                            )
                                            Text(
                                                text = "${s.durationMinutes} min",
                                                fontSize = 11.sp,
                                                color = AuraTextMuted,
                                                modifier = Modifier.padding(top = 2.dp)
                                            )
                                        }

                                        Row(
                                            verticalAlignment = Alignment.CenterVertically,
                                            horizontalArrangement = Arrangement.spacedBy(10.dp)
                                        ) {
                                            Text(
                                                text = s.priceFormatted,
                                                fontSize = 14.sp,
                                                fontWeight = FontWeight.Bold,
                                                color = brandAccent
                                            )

                                            if (uiState.selectedServices.size > 1) {
                                                Box(
                                                    modifier = Modifier
                                                        .size(24.dp)
                                                        .clip(CircleShape)
                                                        .background(AuraPillBg)
                                                        .border(1.dp, AuraBorderSubtle, CircleShape)
                                                        .clickable { onRemoveService(s) },
                                                    contentAlignment = Alignment.Center
                                                ) {
                                                    Icon(
                                                        painter = painterResource(Res.drawable.x),
                                                        contentDescription = "Remover serviço",
                                                        tint = AuraTextMuted,
                                                        modifier = Modifier.size(12.dp)
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }
                            }

                            Spacer(modifier = Modifier.height(14.dp))

                            // Total Bar inside Card
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(brandAccent.copy(alpha = 0.15f), RoundedCornerShape(12.dp))
                                    .border(1.dp, brandAccent.copy(alpha = 0.3f), RoundedCornerShape(12.dp))
                                    .padding(horizontal = 14.dp, vertical = 10.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(
                                    text = "Valor Total Previsto",
                                    fontSize = 13.sp,
                                    color = AuraTextPrimary,
                                    fontWeight = FontWeight.Medium
                                )
                                Text(
                                    text = uiState.totalPriceFormatted,
                                    fontSize = 17.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = brandAccent
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    // 2. Date & Time Selection Card
                    AuraCard(
                        modifier = Modifier.fillMaxWidth(),
                        cornerRadius = 20.dp
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(18.dp)
                        ) {
                            Text(
                                text = "Escolha o Dia do Atendimento",
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Bold,
                                color = AuraTextPrimary
                            )

                            Spacer(modifier = Modifier.height(10.dp))

                            // Date Chips (Horizontal Scrollable Row)
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .horizontalScroll(dateScrollState),
                                horizontalArrangement = Arrangement.spacedBy(10.dp)
                            ) {
                                bookingDays.forEach { day ->
                                    val isSelected = uiState.selectedDateIso == day.dateIso ||
                                        (uiState.selectedDateIso.isBlank() && day.isToday)
                                    val selectedTextCol = AuraOnEmerald
                                    val chipTextCol = if (isSelected) selectedTextCol else AuraTextPrimary
                                    val chipSubtextCol = if (isSelected) selectedTextCol.copy(alpha = 0.85f) else AuraTextMuted

                                    Box(
                                        modifier = Modifier
                                            .width(66.dp)
                                            .height(80.dp)
                                            .clip(RoundedCornerShape(16.dp))
                                            .background(
                                                if (isSelected) brandAccent else AuraPillBg
                                            )
                                            .border(
                                                width = if (isSelected) 1.5.dp else 1.dp,
                                                color = if (isSelected) brandAccent else AuraBorderSubtle,
                                                shape = RoundedCornerShape(16.dp)
                                            )
                                            .clickable { onDateSelect(day.dateIso, day.label) }
                                            .padding(vertical = 8.dp, horizontal = 4.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Column(
                                            horizontalAlignment = Alignment.CenterHorizontally,
                                            verticalArrangement = Arrangement.SpaceBetween,
                                            modifier = Modifier.fillMaxHeight()
                                        ) {
                                            Text(
                                                text = day.weekdayShort.uppercase(),
                                                fontSize = 11.sp,
                                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.SemiBold,
                                                color = chipSubtextCol
                                            )
                                            Text(
                                                text = day.dayOfMonth.toString(),
                                                fontSize = 20.sp,
                                                fontWeight = FontWeight.ExtraBold,
                                                color = chipTextCol
                                            )
                                            if (day.isToday) {
                                                Box(
                                                    modifier = Modifier
                                                        .clip(RoundedCornerShape(4.dp))
                                                        .background(
                                                            if (isSelected) selectedTextCol.copy(alpha = 0.15f)
                                                            else brandAccent.copy(alpha = 0.2f)
                                                        )
                                                        .padding(horizontal = 5.dp, vertical = 1.dp)
                                                ) {
                                                    Text(
                                                        text = "HOJE",
                                                        fontSize = 8.sp,
                                                        fontWeight = FontWeight.Bold,
                                                        color = if (isSelected) selectedTextCol else brandAccent
                                                    )
                                                }
                                            } else {
                                                Text(
                                                    text = day.monthShort,
                                                    fontSize = 10.sp,
                                                    fontWeight = FontWeight.Medium,
                                                    color = if (isSelected) chipSubtextCol else AuraTextMuted
                                                )
                                            }
                                        }
                                    }
                                }
                            }

                            Spacer(modifier = Modifier.height(18.dp))

                            Text(
                                text = "Horários Disponíveis (Bloco de ${uiState.formattedDuration})",
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Bold,
                                color = AuraTextPrimary
                            )

                            Spacer(modifier = Modifier.height(10.dp))

                            if (uiState.availableTimeSlots.isEmpty()) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .background(AuraPillBg, RoundedCornerShape(12.dp))
                                        .border(1.dp, AuraBorderSubtle, RoundedCornerShape(12.dp))
                                        .padding(16.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Text(
                                            text = "Sem horários livres nesta data",
                                            fontSize = 13.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = AuraTextPrimary
                                        )
                                        Text(
                                            text = "O profissional está de folga ou todos os horários para ${uiState.formattedDuration} já foram reservados. Por favor, escolha outro dia acima.",
                                            fontSize = 12.sp,
                                            color = AuraTextMuted,
                                            textAlign = TextAlign.Center,
                                            modifier = Modifier.padding(top = 4.dp)
                                        )
                                    }
                                }
                            } else {
                                Column(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    uiState.availableTimeSlots.chunked(4).forEach { rowSlots ->
                                        Row(
                                            modifier = Modifier.fillMaxWidth(),
                                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                                        ) {
                                            rowSlots.forEach { slot ->
                                                val isSelected = selectedSlot == slot
                                                val slotBg = if (isSelected) brandAccent else AuraPillBg
                                                val slotBorder = if (isSelected) brandAccent else AuraBorderSubtle
                                                val slotText = if (isSelected) AuraOnEmerald else AuraTextPrimary

                                                Box(
                                                    modifier = Modifier
                                                        .weight(1f)
                                                        .clip(RoundedCornerShape(12.dp))
                                                        .background(slotBg)
                                                        .border(
                                                            1.dp,
                                                            slotBorder,
                                                            RoundedCornerShape(12.dp)
                                                        )
                                                        .clickable { onSlotSelect(slot) }
                                                        .padding(vertical = 10.dp),
                                                    contentAlignment = Alignment.Center
                                                ) {
                                                    Text(
                                                        text = slot,
                                                        fontSize = 13.sp,
                                                        fontWeight = if (isSelected) FontWeight.ExtraBold else FontWeight.Bold,
                                                        color = slotText
                                                    )
                                                }
                                            }
                                            repeat(4 - rowSlots.size) {
                                                Spacer(modifier = Modifier.weight(1f))
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    // 3. Client Information Card
                    AuraCard(
                        modifier = Modifier.fillMaxWidth(),
                        cornerRadius = 20.dp
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(18.dp)
                        ) {
                            Text(
                                text = "Seus Dados de Contato",
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Bold,
                                color = AuraTextPrimary
                            )

                            Spacer(modifier = Modifier.height(14.dp))

                            AuraTextField(
                                value = uiState.clientName,
                                onValueChange = onNameChange,
                                label = "Seu Nome Completo",
                                placeholder = "Ex: Camila Silva"
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            AuraTextField(
                                value = uiState.clientPhone,
                                onValueChange = onPhoneChange,
                                label = "Seu WhatsApp",
                                placeholder = "(11) 98765-4321",
                                visualTransformation = PhoneVisualTransformation(),
                                keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(
                                    keyboardType = androidx.compose.ui.text.input.KeyboardType.Number
                                )
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            AuraTextField(
                                value = uiState.clientNotes,
                                onValueChange = onNotesChange,
                                label = "Observações (Opcional)",
                                placeholder = "Ex: Preferência por atendimento pontual"
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(20.dp))

                    // 4. Action Buttons
                    if (uiState.hasReachedMonthlyLimit) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(12.dp))
                                .background(AuraAmber.copy(alpha = 0.12f))
                                .border(1.dp, AuraAmber.copy(alpha = 0.35f), RoundedCornerShape(12.dp))
                                .padding(14.dp)
                        ) {
                            Text(
                                text = "Atenção: Os agendamentos online automáticos deste estabelecimento atingiram a capacidade mensal. Para agendar seu horário, fale diretamente pelo WhatsApp.",
                                color = AuraAmber,
                                fontSize = 12.sp,
                                lineHeight = 16.sp
                            )
                        }
                        Spacer(modifier = Modifier.height(14.dp))

                        val phoneDigits = uiState.professionalPhone?.filter { it.isDigit() }.orEmpty()
                        if (phoneDigits.isNotBlank()) {
                            val national = if (phoneDigits.startsWith("55")) phoneDigits else "55$phoneDigits"
                            val servicesText = uiState.selectedServiceNames
                            val slotText = selectedSlot ?: ""
                            val dateText = uiState.selectedDateText
                            val nameText = uiState.clientName.ifBlank { "Cliente" }
                            val msg = "Olá! Gostaria de agendar no ${uiState.studioName}: $servicesText para $dateText às $slotText. Meu nome é $nameText."
                            AuraButton(
                                text = "Enviar Pedido pelo WhatsApp",
                                onClick = {
                                    uriHandler.openUri("https://wa.me/$national?text=${encodeUri(msg)}")
                                },
                                modifier = Modifier.fillMaxWidth(),
                                variant = AuraButtonVariant.Emerald,
                                size = AuraButtonSize.Large,
                                leadingIcon = {
                                    Icon(
                                        painter = painterResource(Res.drawable.whatsapp_logo),
                                        contentDescription = null,
                                        modifier = Modifier.size(18.dp)
                                    )
                                }
                            )
                            Spacer(modifier = Modifier.height(10.dp))
                        }
                    } else {
                        val confirmTextColor = AuraOnEmerald
                        AuraButton(
                            text = if (uiState.isSubmitting) "Confirmando agendamento..." else if (selectedSlot == null) "Escolha uma data com horário livre" else "Confirmar Agendamento",
                            onClick = onSubmitBooking,
                            modifier = Modifier.fillMaxWidth(),
                            variant = AuraButtonVariant.Emerald,
                            containerColor = brandAccent,
                            contentColor = confirmTextColor,
                            size = AuraButtonSize.Large,
                            enabled = uiState.clientName.isNotBlank() && !uiState.isSubmitting && selectedSlot != null,
                            isLoading = uiState.isSubmitting,
                            leadingIcon = {
                                Icon(
                                    painter = painterResource(Res.drawable.check),
                                    contentDescription = null,
                                    tint = confirmTextColor,
                                    modifier = Modifier.size(18.dp)
                                )
                            }
                        )
                        Spacer(modifier = Modifier.height(10.dp))
                    }

                    AuraButton(
                        text = "Voltar e Alterar Serviços",
                        onClick = onBackToCatalog,
                        modifier = Modifier.fillMaxWidth(),
                        variant = AuraButtonVariant.Text,
                        size = AuraButtonSize.Medium
                    )

                    Spacer(modifier = Modifier.height(32.dp))
                }
            }
        }
    }
}

@Composable
fun PublicBookingSuccessView(
    appointment: br.com.heyagenda.domain.model.Appointment,
    studioName: String,
    totalDuration: String,
    selectedDate: String = "",
    address: String? = null,
    instagramHandle: String? = null,
    onDone: () -> Unit,
    modifier: Modifier = Modifier
) {
    val uriHandler = androidx.compose.ui.platform.LocalUriHandler.current

    val dateDetails = if (selectedDate.isNotBlank()) "Data: $selectedDate\n" else ""
    val gcalTitle = "Agendamento: ${appointment.serviceName} - $studioName"
    val gcalDetails = "Agendamento confirmado no $studioName.\n${dateDetails}Horário: ${appointment.startTime} - ${appointment.endTime}\nServiços: ${appointment.serviceName}\nValor: ${appointment.priceFormatted}\nCliente: ${appointment.clientName}"
    val gcalDatesParam = if (appointment.startUtcIso.isNotBlank() && appointment.endUtcIso.isNotBlank()) {
        val startFormatted = appointment.startUtcIso.replace("-", "").replace(":", "")
        val endFormatted = appointment.endUtcIso.replace("-", "").replace(":", "")
        "&dates=${startFormatted}/${endFormatted}"
    } else ""
    val gcalUrl = "https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeUri(gcalTitle)}&details=${encodeUri(gcalDetails)}&location=${encodeUri(studioName)}${gcalDatesParam}"

    val dateWaText = if (selectedDate.isNotBlank()) " no dia $selectedDate" else ""
    val waMsg = "Olá! Acabei de agendar pelo Hey Agenda no $studioName: ${appointment.serviceName}${dateWaText} às ${appointment.startTime}. Meu nome é ${appointment.clientName}."
    val waUrl = "https://api.whatsapp.com/send?text=${encodeUri(waMsg)}"

    val scrollState = rememberScrollState()

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(AuraBackground)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(scrollState),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Spacer(modifier = Modifier.height(36.dp))

            Column(
                modifier = Modifier
                    .widthIn(max = 560.dp)
                    .fillMaxWidth()
                    .padding(horizontal = 20.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Celebration Icon
                Box(
                    modifier = Modifier
                        .size(72.dp)
                        .clip(CircleShape)
                        .background(AuraEmerald.copy(alpha = 0.15f))
                        .border(2.dp, AuraEmerald, CircleShape),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        painter = painterResource(Res.drawable.check),
                        contentDescription = "Sucesso",
                        tint = AuraEmerald,
                        modifier = Modifier.size(36.dp)
                    )
                }

                Spacer(modifier = Modifier.height(16.dp))

                Text(
                    text = "Agendamento Confirmado!",
                    fontSize = 24.sp,
                    fontWeight = FontWeight.Bold,
                    color = AuraEmerald,
                    textAlign = TextAlign.Center
                )

                Spacer(modifier = Modifier.height(6.dp))

                Text(
                    text = "Seu horário no $studioName está garantido.",
                    fontSize = 14.sp,
                    color = AuraTextMuted,
                    textAlign = TextAlign.Center
                )

                Spacer(modifier = Modifier.height(24.dp))

                // Summary Card
                AuraCard(
                    modifier = Modifier.fillMaxWidth(),
                    cornerRadius = 20.dp,
                    borderColor = AuraEmerald.copy(alpha = 0.5f)
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(20.dp)
                    ) {
                        Text(
                            text = appointment.serviceName,
                            fontSize = 17.sp,
                            fontWeight = FontWeight.Bold,
                            color = AuraTextPrimary
                        )

                        Spacer(modifier = Modifier.height(8.dp))

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(6.dp)
                        ) {
                            if (selectedDate.isNotBlank()) {
                                Icon(
                                    painter = painterResource(Res.drawable.calendar_blank),
                                    contentDescription = null,
                                    tint = AuraEmerald,
                                    modifier = Modifier.size(13.dp)
                                )
                                Text(
                                    text = selectedDate,
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    color = AuraTextPrimary
                                )
                                Text(
                                    text = "•",
                                    fontSize = 13.sp,
                                    color = AuraTextMuted
                                )
                            }
                            Text(
                                text = "${appointment.startTime} - ${appointment.endTime}",
                                fontSize = 13.sp,
                                color = AuraTextMuted
                            )
                            Text(
                                text = "•",
                                fontSize = 13.sp,
                                color = AuraTextMuted
                            )
                            Icon(
                                painter = painterResource(Res.drawable.clock),
                                contentDescription = null,
                                tint = AuraTextMuted,
                                modifier = Modifier.size(13.dp)
                            )
                            Text(
                                text = totalDuration,
                                fontSize = 13.sp,
                                color = AuraTextMuted
                            )
                        }

                        Spacer(modifier = Modifier.height(6.dp))

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = "Cliente: ${appointment.clientName}",
                                fontSize = 13.sp,
                                color = AuraTextMuted
                            )
                            Text(
                                text = appointment.priceFormatted,
                                fontSize = 18.sp,
                                fontWeight = FontWeight.Bold,
                                color = AuraEmerald
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Action 1: Google Calendar
                AuraButton(
                    text = "Adicionar ao Google Agenda",
                    onClick = {
                        try {
                            uriHandler.openUri(gcalUrl)
                        } catch (_: Throwable) {}
                    },
                    modifier = Modifier.fillMaxWidth(),
                    variant = AuraButtonVariant.Secondary,
                    size = AuraButtonSize.Medium,
                    leadingIcon = {
                        Icon(
                            painter = painterResource(Res.drawable.calendar_blank),
                            contentDescription = null,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                )

                Spacer(modifier = Modifier.height(10.dp))

                // Action 2: WhatsApp
                AuraButton(
                    text = "Abrir no WhatsApp",
                    onClick = {
                        try {
                            uriHandler.openUri(waUrl)
                        } catch (_: Throwable) {}
                    },
                    modifier = Modifier.fillMaxWidth(),
                    variant = AuraButtonVariant.Secondary,
                    size = AuraButtonSize.Medium,
                    leadingIcon = {
                        Icon(
                            painter = painterResource(Res.drawable.whatsapp_logo),
                            contentDescription = null,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                )

                // Action: Google Maps
                if (!address.isNullOrBlank()) {
                    val cleanAddr = address.trim()
                    val mapsUrl = "https://www.google.com/maps/search/?api=1&query=${encodeUri(cleanAddr)}"
                    Spacer(modifier = Modifier.height(10.dp))
                    AuraButton(
                        text = "Ver Endereço no Google Maps",
                        onClick = {
                            try {
                                uriHandler.openUri(mapsUrl)
                            } catch (_: Throwable) {}
                        },
                        modifier = Modifier.fillMaxWidth(),
                        variant = AuraButtonVariant.Secondary,
                        size = AuraButtonSize.Medium,
                        leadingIcon = {
                            Icon(
                                painter = painterResource(Res.drawable.map_pin_simple),
                                contentDescription = null,
                                modifier = Modifier.size(16.dp)
                            )
                        }
                    )
                }

                // Action: Instagram
                if (!instagramHandle.isNullOrBlank()) {
                    val cleanHandle = instagramHandle.trim().removePrefix("@")
                    val igUrl = "https://instagram.com/$cleanHandle"
                    Spacer(modifier = Modifier.height(10.dp))
                    AuraButton(
                        text = "Seguir @$cleanHandle no Instagram",
                        onClick = {
                            try {
                                uriHandler.openUri(igUrl)
                            } catch (_: Throwable) {}
                        },
                        modifier = Modifier.fillMaxWidth(),
                        variant = AuraButtonVariant.Secondary,
                        size = AuraButtonSize.Medium,
                        leadingIcon = {
                            Icon(
                                painter = painterResource(Res.drawable.instagram_logo),
                                contentDescription = null,
                                modifier = Modifier.size(16.dp)
                            )
                        }
                    )
                }

                Spacer(modifier = Modifier.height(14.dp))

                // Action 3: Novo Agendamento / Concluir
                AuraButton(
                    text = "Concluir / Novo Agendamento",
                    onClick = onDone,
                    modifier = Modifier.fillMaxWidth(),
                    variant = AuraButtonVariant.Primary,
                    size = AuraButtonSize.Large,
                    trailingIcon = {
                        Icon(
                            painter = painterResource(Res.drawable.check),
                            contentDescription = null,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                )

                Spacer(modifier = Modifier.height(36.dp))
            }
        }
    }
}


private fun encodeUri(text: String): String {
    return text
        .replace(" ", "%20")
        .replace("\n", "%0A")
        .replace(":", "%3A")
        .replace("-", "%2D")
        .replace("+", "%2B")
        .replace("&", "%26")
        .replace("?", "%3F")
        .replace("=", "%3D")
}
